import { useMemo } from 'react';
import { useAuthContext } from '../../../auth/contexts/AuthContext';
import useGraphQLClient from '../../../../app/hooks/useGraphQLClient';
import { useQuery } from '@tanstack/react-query';
import { gql } from 'graphql-request';
import IFundPerformances from '../../interfaces/fund/performances/IFundPerformances';
import IFundFilters from '../../interfaces/fund/IFundFilters';
import AppError from '../../../../app/features/error/services/AppError';
import _ from 'lodash';

const useInvestFundPerformances = (fundId: number | null, filters: IFundFilters) => {
	const auth = useAuthContext();
	const graphQLClient = useGraphQLClient();
	const filter = {
		fundId: fundId,
		userId: auth.authOverrideUserId,
		investorIds: filters.investorIds ?? null,
	};

	const { data, isFetching } = useQuery(
		['investmentFundPerformances', auth.authOverrideUserId ?? 'null', filter],
		async () => {
			try {
				const data = (await graphQLClient.request(
					gql`
						query ($fundId: Int!, $investorIds: [Int]) {
							investmentFundPerformances(fundId: $fundId, investorIds: $investorIds) {
								capital {
									dataUpdatedOn
									isEstimated
									kpis {
										committed {
											value
											total
										}
										called {
											value
											total
										}
										callable {
											value
											total
										}
										distributedInterestIncome {
											value
											total
										}
										distributedReturnOfCapital {
											value
											total
										}
										netCalled {
											value
											total
										}
										assetOutstandingCapital {
											value
											total
										}
										distributions {
											value
											total
										}
										distributionsRecallable {
											value
											total
										}
										fees {
											value
											total
										}
									}
								}
								performance {
									dataUpdatedOn
									isEstimated
									isLiquidationQuarter
									reportingPeriod {
										tag
										name
									}
									kpisPosition {
										currency
										netAssetValue
										irr
										irrIsNs
										irrGross
										irrGrossIsN
										arr
										arrIsNs
										totalValue
										tvpi
										dpi
										rvpi
									}
									kpisTotal {
										currency
										netAssetValue
										irr
										irrIsNs
										irrGross
										irrGrossIsN
										arr
										arrIsNs
										totalValue
										tvpi
										dpi
										rvpi
									}
								}
								portfolio {
									dataUpdatedOn
									isEstimated
									isLiquidationQuarter
									reportingPeriod {
										tag
										name
									}
									kpis {
										capitalInvested
										realizedGains
										unrealizedGains
										percentOfCapitalCalledInvested
										totalValue
										moic
										currency
									}
								}
							}
						}
					`,
					filter,
				)) as { investmentFundPerformances: IFundPerformances };
				return data.investmentFundPerformances;
			} catch (e) {
				throw new AppError(e, 'useInvestFundPerformances');
			}
		},
		{
			enabled:
				!!filter.fundId && auth.isInvestor === true && auth.isAuthenticated && auth.currentCompany !== null,
		},
	);

	const fundPerformances = useMemo(() => (data ? _.cloneDeep(data) : undefined), [data]);

	return {
		isFetching,
		capital: fundPerformances ? fundPerformances.capital ?? null : null,
		performance: fundPerformances ? fundPerformances.performance : null,
		portfolio: fundPerformances ? fundPerformances.portfolio : null,
	};
};

export default useInvestFundPerformances;
